/**
 * IB Diploma points and the diploma's failing conditions.
 *
 * Source: International Baccalaureate, *Diploma Programme Assessment procedures 2025*
 * (published September 2024, updated November 2024, February 2025 and May 2025):
 * section A2.2.1 (subjects graded 1–7, TOK and the EE graded A–E and combined in the
 * diploma points matrix for 0–3 points, maximum 45, minimum 24), section A2.2.2 and the
 * diploma requirement codes in C8.2.4 (the failing conditions below), Figure 8 (the
 * TOK/EE matrix), and C1.1.2 (a diploma is four HL + two SL or three HL + three SL).
 *
 * Not modelled: an "N" (no grade) for a subject or core component, and a penalty for
 * academic misconduct — both are also failing conditions.
 */

export type IbLevel = "HL" | "SL";
export type IbCoreGrade = "A" | "B" | "C" | "D" | "E";

export interface IbSubject {
  level: IbLevel;
  /** 1–7. */
  grade: number;
}

export const IB_CORE_GRADES: readonly IbCoreGrade[] = ["A", "B", "C", "D", "E"];

/**
 * Figure 8, rows = extended essay, columns = TOK. Null is a failing condition (any E).
 */
const CORE_MATRIX: Record<IbCoreGrade, Record<IbCoreGrade, number | null>> = {
  A: { A: 3, B: 3, C: 2, D: 2, E: null },
  B: { A: 3, B: 2, C: 2, D: 1, E: null },
  C: { A: 2, B: 2, C: 1, D: 0, E: null },
  D: { A: 2, B: 1, C: 0, D: 0, E: null },
  E: { A: null, B: null, C: null, D: null, E: null },
};

export function corePoints(tok: IbCoreGrade, ee: IbCoreGrade): number | null {
  return CORE_MATRIX[ee][tok];
}

export type IbFailure =
  | "cas"
  | "total"
  | "core-e"
  | "grade-1"
  | "grade-2"
  | "grade-3"
  | "hl"
  | "sl";

export interface IbResult {
  subjectPoints: number;
  core: number | null;
  total: number;
  hlCount: number;
  hlPoints: number;
  slPoints: number;
  /** The SL minimum that applies: 9, or 5 for a student with two SL subjects. */
  slMinimum: number;
  failures: IbFailure[];
}

function sumOf(values: readonly number[]): number {
  let total = 0;
  for (const value of values) {
    total += value;
  }
  return total;
}

/** With four HL subjects, only the three highest count toward the 12-point rule. */
function hlPointsOf(grades: readonly number[]): number {
  const sorted = [...grades].sort((a, b) => b - a);
  return sumOf(sorted.slice(0, 3));
}

/** True when the subject mix is a diploma combination: six subjects, three or four HL. */
export function isDiplomaCombination(subjects: readonly IbSubject[]): boolean {
  const hl = subjects.filter((subject) => subject.level === "HL").length;
  return subjects.length === 6 && (hl === 3 || hl === 4);
}

function gradeFailures(grades: readonly number[]): IbFailure[] {
  const failures: IbFailure[] = [];
  if (grades.some((grade) => grade === 1)) {
    failures.push("grade-1");
  }
  if (grades.filter((grade) => grade === 2).length >= 3) {
    failures.push("grade-2");
  }
  if (grades.filter((grade) => grade <= 3).length >= 4) {
    failures.push("grade-3");
  }
  return failures;
}

export function ibResult(
  subjects: readonly IbSubject[],
  tok: IbCoreGrade,
  ee: IbCoreGrade,
  casComplete: boolean
): IbResult {
  const grades = subjects.map((subject) => subject.grade);
  const hlGrades = subjects
    .filter((subject) => subject.level === "HL")
    .map((subject) => subject.grade);
  const slGrades = subjects
    .filter((subject) => subject.level === "SL")
    .map((subject) => subject.grade);
  const core = corePoints(tok, ee);
  const subjectPoints = sumOf(grades);
  const total = subjectPoints + (core ?? 0);
  const hlPoints = hlPointsOf(hlGrades);
  const slPoints = sumOf(slGrades);
  const slMinimum = slGrades.length === 2 ? 5 : 9;

  const failures: IbFailure[] = [];
  if (!casComplete) {
    failures.push("cas");
  }
  if (total < 24) {
    failures.push("total");
  }
  if (core === null) {
    failures.push("core-e");
  }
  failures.push(...gradeFailures(grades));
  if (hlPoints < 12) {
    failures.push("hl");
  }
  if (slPoints < slMinimum) {
    failures.push("sl");
  }
  return {
    subjectPoints,
    core,
    total,
    hlCount: hlGrades.length,
    hlPoints,
    slPoints,
    slMinimum,
    failures,
  };
}
